import { useState, useEffect, useRef } from "react";
import "./Payroll.css";

const API = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");

/* ── helpers ── */
const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n ?? 0);
const fmtHr = (h) => `${Number(h ?? 0).toFixed(1)} hrs`;
const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const now = new Date();

function avatar(name = "") {
  const parts = name.trim().split(" ");
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

function StatusBadge({ status }) {
  const map = { paid: "badge-paid", pending: "badge-pending", processing: "badge-processing", draft: "badge-draft" };
  return <span className={`pr-badge ${map[status] ?? "badge-draft"}`}>{status}</span>;
}

function monthDays(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

/* ══════════════════════════════════
   PAYROLL MODULE
══════════════════════════════════ */
export default function Payroll() {
  const [tab, setTab] = useState("dashboard");
  const [employees, setEmployees] = useState([]);
  const [payrolls, setPayrolls]   = useState([]);
  const [settings, setSettings]   = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState(null);
  const [month, setMonth]   = useState(now.getMonth());
  const [year, setYear]     = useState(now.getFullYear());

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }

  async function load() {
    setLoading(true);
    try {
      const [empRes, payRes, setRes, attRes] = await Promise.all([
        fetch(`${API}/api/payroll/employees`, { headers }),
        fetch(`${API}/api/payroll/records?month=${month + 1}&year=${year}`, { headers }),
        fetch(`${API}/api/payroll/settings`, { headers }),
        fetch(`${API}/api/payroll/attendance?month=${month + 1}&year=${year}`, { headers }),
      ]);
      const empData = await empRes.json();
      const payData = await payRes.json();
      const setData = await setRes.json();
      const attData = await attRes.json();
      setEmployees(empData.data ?? []);
      setPayrolls(payData.data ?? []);
      setSettings(setData.data ?? defaultSettings);
      setAttendance(attData.data ?? []);
    } catch (e) {
      // Fallback: load what we can
      try {
        const empRes = await fetch(`${API}/api/payroll/employees`, { headers });
        const empData = await empRes.json();
        setEmployees(empData.data ?? []);
      } catch {}
      setPayrolls([]);
      setSettings(defaultSettings);
      setAttendance([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [month, year]);

  const totalGross  = payrolls.reduce((s, p) => s + (p.gross_salary ?? 0), 0);
  const totalNet    = payrolls.reduce((s, p) => s + (p.net_salary ?? 0), 0);
  const totalDeduct = payrolls.reduce((s, p) => s + (p.total_deductions ?? 0), 0);
  const paidCount   = payrolls.filter(p => p.status === "paid").length;

  return (
    <div className="pr-root">
      {/* ── Sidebar nav ── */}
      <aside className="pr-sidebar">
        <div className="pr-sidebar-brand">
          <div className="pr-sidebar-icon">₹</div>
          <span>PayRoll</span>
        </div>
        <nav className="pr-sidebar-nav">
          {[
            { id: "dashboard",  icon: "⬡", label: "Dashboard" },
            { id: "attendance", icon: "📅", label: "Attendance" },
            { id: "run",        icon: "▶", label: "Run Payroll" },
            { id: "records",    icon: "⊞", label: "Pay Records" },
            { id: "employees",  icon: "⊙", label: "Employees" },
            { id: "settings",   icon: "◎", label: "Settings" },
          ].map(n => (
            <button
              key={n.id}
              className={`pr-nav-item${tab === n.id ? " active" : ""}`}
              onClick={() => setTab(n.id)}
            >
              <span className="pr-nav-icon">{n.icon}</span>
              <span>{n.label}</span>
            </button>
          ))}
        </nav>
        <div className="pr-sidebar-footer">
          <div className="pr-month-picker">
            <select value={month} onChange={e => setMonth(Number(e.target.value))}>
              {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="pr-main">
        <header className="pr-topbar">
          <div className="pr-topbar-left">
            <h1 className="pr-page-title">
              {tab === "dashboard"  && "Payroll Dashboard"}
              {tab === "attendance" && "Attendance Manager"}
              {tab === "run"        && "Run Payroll"}
              {tab === "records"    && "Pay Records"}
              {tab === "employees"  && "Employee Master"}
              {tab === "settings"   && "Payroll Settings"}
            </h1>
            <span className="pr-period-chip">{months[month]} {year}</span>
          </div>
          <button className="pr-refresh-btn" onClick={load} title="Refresh">↻</button>
        </header>

        {loading ? (
          <div className="pr-loader">
            <div className="pr-spinner" />
            <p>Loading payroll data…</p>
          </div>
        ) : (
          <div className="pr-content">
            {tab === "dashboard" && (
              <Dashboard
                payrolls={payrolls} employees={employees}
                totalGross={totalGross} totalNet={totalNet}
                totalDeduct={totalDeduct} paidCount={paidCount}
                month={month} year={year}
                attendance={attendance}
              />
            )}
            {tab === "attendance" && (
              <AttendanceManager
                employees={employees}
                attendance={attendance}
                headers={headers}
                month={month} year={year}
                onUpdate={load}
                onToast={showToast}
              />
            )}
            {tab === "run" && (
              <RunPayroll
                employees={employees} payrolls={payrolls}
                settings={settings} headers={headers}
                month={month} year={year}
                attendance={attendance}
                onDone={() => { load(); showToast("Payroll processed successfully!"); }}
                onError={(m) => showToast(m, "error")}
              />
            )}
            {tab === "records" && (
              <PayRecords payrolls={payrolls} headers={headers}
                onUpdate={load} onToast={showToast} month={month} year={year} />
            )}
            {tab === "employees" && (
              <Employees employees={employees} headers={headers}
                onUpdate={load} onToast={showToast} />
            )}
            {tab === "settings" && (
              <Settings settings={settings} headers={headers}
                onUpdate={load} onToast={showToast} />
            )}
          </div>
        )}
      </main>

      {/* ── Toast ── */}
      {toast && (
        <div className={`pr-toast ${toast.type}`}>
          <span>{toast.type === "success" ? "✓" : "✕"}</span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   DASHBOARD
══════════════════════════════════ */
function Dashboard({ payrolls, employees, totalGross, totalNet, totalDeduct, paidCount, month, year, attendance }) {
  const pending = payrolls.filter(p => p.status !== "paid").length;
  const totalDays = monthDays(year, month);

  // Attendance summary
  const totalFullLeaves = attendance.reduce((s, a) => s + (a.full_day_leaves ?? 0), 0);
  const totalHalfLeaves = attendance.reduce((s, a) => s + (a.half_day_leaves ?? 0), 0);
  const totalOTHours    = attendance.reduce((s, a) => s + (a.overtime_hours ?? 0), 0);
  const totalExtraHours = attendance.reduce((s, a) => s + (a.extra_hours ?? 0), 0);

  const stats = [
    { label: "Total Gross",    val: fmt(totalGross),  icon: "💰", color: "var(--brand)",  bg: "var(--brand-l)" },
    { label: "Net Disbursed",  val: fmt(totalNet),    icon: "✅", color: "var(--green)",  bg: "var(--green-l)" },
    { label: "Deductions",     val: fmt(totalDeduct), icon: "📉", color: "var(--red)",    bg: "var(--red-l)" },
    { label: "Paid / Total",   val: `${paidCount} / ${payrolls.length}`, icon: "👥", color: "var(--blue)", bg: "var(--blue-l)" },
  ];

  const attStats = [
    { label: "Working Days",   val: totalDays,              icon: "📆", color: "var(--ink-2)", bg: "var(--canvas)" },
    { label: "Full Day Leaves",val: totalFullLeaves,        icon: "🏖️", color: "var(--red)",   bg: "var(--red-l)" },
    { label: "Half Day Leaves",val: totalHalfLeaves,        icon: "🌤️", color: "var(--amber)", bg: "var(--amber-l)" },
    { label: "Overtime Hours", val: fmtHr(totalOTHours + totalExtraHours), icon: "⚡", color: "var(--green)", bg: "var(--green-l)" },
  ];

  return (
    <div className="pr-dashboard">
      <div className="pr-stats-grid">
        {stats.map(s => (
          <div className="pr-stat-card" key={s.label}>
            <div className="pr-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
            <div>
              <div className="pr-stat-val" style={{ color: s.color }}>{s.val}</div>
              <div className="pr-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Attendance stats */}
      <div className="pr-section-title">Attendance Overview — {months[month]} {year}</div>
      <div className="pr-stats-grid" style={{ marginBottom: 20 }}>
        {attStats.map(s => (
          <div className="pr-stat-card" key={s.label}>
            <div className="pr-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
            <div>
              <div className="pr-stat-val" style={{ color: s.color }}>{s.val}</div>
              <div className="pr-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* alert strip */}
      {pending > 0 && (
        <div className="pr-alert-strip">
          <span>⚠️</span>
          <span><strong>{pending}</strong> employees have pending/unpaid salary for {months[month]} {year}.</span>
        </div>
      )}

      {/* Recent payroll table */}
      <div className="pr-card">
        <div className="pr-card-head">
          <span className="pr-card-title">Payroll Summary — {months[month]} {year}</span>
          <span className="pr-card-sub">{payrolls.length} records</span>
        </div>
        {payrolls.length === 0 ? (
          <div className="pr-empty">No payroll records for this period. Run payroll to generate.</div>
        ) : (
          <div className="pr-table-wrap">
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Employee</th><th>Dept</th><th>Working Days</th><th>Leaves</th><th>Hours</th>
                  <th>Gross</th><th>Deductions</th><th>Net Pay</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payrolls.map(p => {
                  const att = attendance.find(a => a.employee_id === p.employee_id) || {};
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="pr-emp-cell">
                          <div className="pr-avatar">{avatar(p.employee_name)}</div>
                          <div>
                            <div className="pr-emp-name">{p.employee_name}</div>
                            <div className="pr-emp-role">{p.designation}</div>
                          </div>
                        </div>
                      </td>
                      <td>{p.department || "—"}</td>
                      <td>{p.working_days ?? "—"}</td>
                      <td>
                        <div className="pr-leave-summary">
                          {(att.full_day_leaves || 0) > 0 && <span className="pr-leave-chip full">{att.full_day_leaves}F</span>}
                          {(att.half_day_leaves || 0) > 0 && <span className="pr-leave-chip half">{att.half_day_leaves}H</span>}
                          {(att.full_day_leaves || 0) === 0 && (att.half_day_leaves || 0) === 0 && <span className="pr-leave-chip none">—</span>}
                        </div>
                      </td>
                      <td>{fmtHr(p.hours_worked)}</td>
                      <td className="pr-money">{fmt(p.gross_salary)}</td>
                      <td className="pr-money red">{fmt(p.total_deductions)}</td>
                      <td className="pr-money green">{fmt(p.net_salary)}</td>
                      <td><StatusBadge status={p.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   ATTENDANCE MANAGER
══════════════════════════════════ */
function AttendanceManager({ employees, attendance, headers, month, year, onUpdate, onToast }) {
  const [search, setSearch]       = useState("");
  const [saving, setSaving]       = useState(false);
  const [editRow, setEditRow]     = useState(null); // employee id being edited inline
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkForm, setBulkForm]   = useState({ type: "full_leave", employees: [], note: "" });
  const [localAtt, setLocalAtt]   = useState([]);
  const totalDays = monthDays(year, month);

  // Initialize local attendance from props
  useEffect(() => {
    const attMap = {};
    attendance.forEach(a => { attMap[a.employee_id] = a; });
    const rows = employees.map(e => {
      const existing = attMap[e.id] || {};
      return {
        employee_id:    e.id,
        employee_name:  `${e.first_name} ${e.last_name}`,
        designation:    e.designation || "",
        department:     e.department  || "",
        total_days:     existing.total_days     ?? totalDays,
        working_days:   existing.working_days   ?? totalDays,
        full_day_leaves: existing.full_day_leaves ?? 0,
        half_day_leaves: existing.half_day_leaves ?? 0,
        unpaid_full:    existing.unpaid_full    ?? 0,
        unpaid_half:    existing.unpaid_half    ?? 0,
        overtime_hours: existing.overtime_hours ?? 0,
        extra_hours:    existing.extra_hours    ?? 0,
        note:           existing.note           ?? "",
        _dirty:         false,
      };
    });
    setLocalAtt(rows);
  }, [employees, attendance, month, year]);

  function updateRow(empId, field, val) {
    setLocalAtt(prev => prev.map(r => {
      if (r.employee_id !== empId) return r;
      const updated = { ...r, [field]: isNaN(val) || val === "" ? val : Number(val), _dirty: true };
      // Auto-recalculate working days
      const fullLeave = field === "full_day_leaves" ? Number(val) : updated.full_day_leaves;
      const halfLeave = field === "half_day_leaves" ? Number(val) : updated.half_day_leaves;
      const totalD    = field === "total_days" ? Number(val) : updated.total_days;
      updated.working_days = Math.max(0, totalD - fullLeave - halfLeave * 0.5);
      return updated;
    }));
  }

  async function saveAll() {
    const dirty = localAtt.filter(r => r._dirty);
    if (dirty.length === 0) { onToast("No changes to save.", "error"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/payroll/attendance`, {
        method: "POST",
        headers,
        body: JSON.stringify({ month: month + 1, year, records: dirty }),
      });
      if (!res.ok) throw new Error("Save failed");
      onToast(`Saved attendance for ${dirty.length} employee(s)!`);
      onUpdate();
    } catch (e) {
      onToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function saveSingle(row) {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/payroll/attendance`, {
        method: "POST",
        headers,
        body: JSON.stringify({ month: month + 1, year, records: [row] }),
      });
      if (!res.ok) throw new Error("Save failed");
      onToast("Attendance saved!");
      setEditRow(null);
      onUpdate();
    } catch (e) {
      onToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function applyBulk() {
    if (bulkForm.employees.length === 0) { onToast("Select at least one employee.", "error"); return; }
    setSaving(true);
    try {
      const updatedRows = localAtt.map(r => {
        if (!bulkForm.employees.includes(r.employee_id)) return r;
        const updated = { ...r, _dirty: true };
        if (bulkForm.type === "full_leave")   { updated.full_day_leaves += 1; }
        if (bulkForm.type === "half_leave")   { updated.half_day_leaves += 1; }
        if (bulkForm.type === "unpaid_full")  { updated.unpaid_full += 1; updated.full_day_leaves += 1; }
        if (bulkForm.type === "unpaid_half")  { updated.unpaid_half += 1; updated.half_day_leaves += 1; }
        if (bulkForm.type === "overtime")     { updated.overtime_hours += Number(bulkForm.hours || 0); }
        if (bulkForm.type === "extra_hours")  { updated.extra_hours += Number(bulkForm.hours || 0); }
        updated.working_days = Math.max(0, updated.total_days - updated.full_day_leaves - updated.half_day_leaves * 0.5);
        if (bulkForm.note) updated.note = bulkForm.note;
        return updated;
      });
      setLocalAtt(updatedRows);

      const dirty = updatedRows.filter(r => bulkForm.employees.includes(r.employee_id));
      const res = await fetch(`${API}/api/payroll/attendance`, {
        method: "POST",
        headers,
        body: JSON.stringify({ month: month + 1, year, records: dirty }),
      });
      if (!res.ok) throw new Error("Save failed");
      onToast(`Applied to ${dirty.length} employee(s)!`);
      setBulkModal(false);
      setBulkForm({ type: "full_leave", employees: [], note: "" });
      onUpdate();
    } catch (e) {
      onToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  function toggleBulkEmp(id) {
    setBulkForm(f => ({
      ...f,
      employees: f.employees.includes(id) ? f.employees.filter(x => x !== id) : [...f.employees, id]
    }));
  }

  function selectAllBulk() {
    setBulkForm(f => ({ ...f, employees: localAtt.map(r => r.employee_id) }));
  }

  const filtered = localAtt.filter(r =>
    `${r.employee_name} ${r.designation} ${r.department}`.toLowerCase().includes(search.toLowerCase())
  );

  const dirtyCount = localAtt.filter(r => r._dirty).length;

  return (
    <div className="pr-attendance">
      {/* Toolbar */}
      <div className="pr-toolbar">
        <div className="pr-search-wrap">
          <span className="pr-search-icon">🔍</span>
          <input className="pr-search" placeholder="Search employee…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="pr-toolbar-right">
          <span className="pr-chip">{filtered.length} employees</span>
          <span className="pr-chip" style={{ background: "var(--canvas)" }}>
            📆 {totalDays} days in {months[month]}
          </span>
          {dirtyCount > 0 && <span className="pr-chip amber">{dirtyCount} unsaved</span>}
          <button className="pr-btn pr-btn-secondary" onClick={() => { setBulkModal(true); setBulkForm({ type: "full_leave", employees: [], note: "" }); }}>
            ⚡ Bulk Action
          </button>
          <button className="pr-btn pr-btn-primary" onClick={saveAll} disabled={saving}>
            {saving ? "Saving…" : `💾 Save All Changes`}
          </button>
        </div>
      </div>

      {/* Info strip */}
      <div className="pr-info-strip">
        <span>📌 <strong>Working Days</strong> are auto-calculated: Total Days − Full Leaves − (Half Leaves × 0.5)</span>
        <span>• <strong>Paid Leaves</strong> still count in Working Days for salary calculation</span>
        <span>• <strong>Unpaid</strong> deducts from earned salary</span>
      </div>

      {/* Table */}
      <div className="pr-card" style={{ overflow: "visible" }}>
        <div className="pr-card-head">
          <span className="pr-card-title">Monthly Attendance — {months[month]} {year}</span>
          <span className="pr-card-sub">Click any row to edit inline</span>
        </div>
        <div className="pr-table-wrap">
          <table className="pr-table pr-att-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th title="Total calendar days in month">Total Days</th>
                <th title="Paid full day leaves">Paid Full Leaves</th>
                <th title="Paid half day leaves">Paid Half Leaves</th>
                <th title="Unpaid full day absences">Unpaid Full</th>
                <th title="Unpaid half day absences">Unpaid Half</th>
                <th title="Auto-calculated">Eff. Working Days</th>
                <th>Overtime Hrs</th>
                <th>Extra Hrs</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const isEditing = editRow === r.employee_id;
                return (
                  <tr key={r.employee_id} className={`${r._dirty ? "pr-row-dirty" : ""} ${isEditing ? "pr-row-editing" : ""}`}>
                    <td>
                      <div className="pr-emp-cell">
                        <div className="pr-avatar sm">{avatar(r.employee_name)}</div>
                        <div>
                          <div className="pr-emp-name">{r.employee_name}</div>
                          <div className="pr-emp-role">{r.designation} · {r.department}</div>
                        </div>
                      </div>
                    </td>

                    {/* Total Days */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell narrow" type="number" min="1" max="31"
                            value={r.total_days} onChange={e => updateRow(r.employee_id, "total_days", e.target.value)} />
                        : <span className="pr-att-val">{r.total_days}</span>
                      }
                    </td>

                    {/* Paid Full Leave */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell narrow" type="number" min="0" max={r.total_days}
                            value={r.full_day_leaves} onChange={e => updateRow(r.employee_id, "full_day_leaves", e.target.value)} />
                        : <span className={`pr-att-chip ${r.full_day_leaves > 0 ? "leave-full" : "leave-zero"}`}>{r.full_day_leaves}</span>
                      }
                    </td>

                    {/* Paid Half Leave */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell narrow" type="number" min="0" max={r.total_days}
                            value={r.half_day_leaves} onChange={e => updateRow(r.employee_id, "half_day_leaves", e.target.value)} />
                        : <span className={`pr-att-chip ${r.half_day_leaves > 0 ? "leave-half" : "leave-zero"}`}>{r.half_day_leaves}</span>
                      }
                    </td>

                    {/* Unpaid Full */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell narrow" type="number" min="0"
                            value={r.unpaid_full} onChange={e => updateRow(r.employee_id, "unpaid_full", e.target.value)} />
                        : <span className={`pr-att-chip ${r.unpaid_full > 0 ? "leave-unpaid" : "leave-zero"}`}>{r.unpaid_full}</span>
                      }
                    </td>

                    {/* Unpaid Half */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell narrow" type="number" min="0"
                            value={r.unpaid_half} onChange={e => updateRow(r.employee_id, "unpaid_half", e.target.value)} />
                        : <span className={`pr-att-chip ${r.unpaid_half > 0 ? "leave-unpaid" : "leave-zero"}`}>{r.unpaid_half}</span>
                      }
                    </td>

                    {/* Effective working days */}
                    <td>
                      <span className="pr-att-working">{Number(r.working_days).toFixed(1)}</span>
                    </td>

                    {/* Overtime */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell narrow" type="number" min="0" step="0.5"
                            value={r.overtime_hours} onChange={e => updateRow(r.employee_id, "overtime_hours", e.target.value)} />
                        : <span className={`pr-att-chip ${r.overtime_hours > 0 ? "ot" : "leave-zero"}`}>{r.overtime_hours > 0 ? `+${r.overtime_hours}h` : "—"}</span>
                      }
                    </td>

                    {/* Extra Hours */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell narrow" type="number" min="0" step="0.5"
                            value={r.extra_hours} onChange={e => updateRow(r.employee_id, "extra_hours", e.target.value)} />
                        : <span className={`pr-att-chip ${r.extra_hours > 0 ? "ot" : "leave-zero"}`}>{r.extra_hours > 0 ? `+${r.extra_hours}h` : "—"}</span>
                      }
                    </td>

                    {/* Note */}
                    <td>
                      {isEditing
                        ? <input className="pr-input-cell wide" type="text" placeholder="Optional note"
                            value={r.note} onChange={e => updateRow(r.employee_id, "note", e.target.value)} />
                        : <span className="pr-att-note">{r.note || "—"}</span>
                      }
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="pr-row-actions">
                        {isEditing ? (
                          <>
                            <button className="pr-act-btn success" onClick={() => saveSingle(r)} disabled={saving} title="Save">✓</button>
                            <button className="pr-act-btn" onClick={() => setEditRow(null)} title="Cancel">✕</button>
                          </>
                        ) : (
                          <>
                            <button className="pr-act-btn info" onClick={() => setEditRow(r.employee_id)} title="Edit">✏</button>
                            {r._dirty && <span className="pr-dirty-dot" title="Unsaved changes">●</span>}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="pr-empty">No employees found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="pr-att-legend">
        <span className="pr-legend-item"><span className="pr-att-chip leave-full" style={{minWidth:24,textAlign:"center"}}>F</span> Paid Full-Day Leave (counted in salary)</span>
        <span className="pr-legend-item"><span className="pr-att-chip leave-half" style={{minWidth:24,textAlign:"center"}}>H</span> Paid Half-Day Leave (counted in salary)</span>
        <span className="pr-legend-item"><span className="pr-att-chip leave-unpaid" style={{minWidth:24,textAlign:"center"}}>U</span> Unpaid Absence (deducted from salary)</span>
        <span className="pr-legend-item"><span className="pr-att-chip ot" style={{minWidth:24,textAlign:"center"}}>⚡</span> Overtime / Extra Hours (added to salary)</span>
      </div>

      {/* Bulk Action Modal */}
      {bulkModal && (
        <div className="pr-modal-overlay" onClick={() => setBulkModal(false)}>
          <div className="pr-modal" onClick={e => e.stopPropagation()}>
            <div className="pr-modal-head">
              <h3>⚡ Bulk Attendance Action</h3>
              <button className="pr-modal-close" onClick={() => setBulkModal(false)}>✕</button>
            </div>
            <div style={{ padding: "20px" }}>
              {/* Action type */}
              <div className="pr-field" style={{ marginBottom: 16 }}>
                <label>Action Type</label>
                <div className="pr-bulk-type-grid">
                  {[
                    { val: "full_leave",   label: "✅ Full Day Leave (Paid)",    color: "var(--green)" },
                    { val: "half_leave",   label: "🌤️ Half Day Leave (Paid)",    color: "var(--amber)" },
                    { val: "unpaid_full",  label: "❌ Full Day Absent (Unpaid)", color: "var(--red)" },
                    { val: "unpaid_half",  label: "⚠️ Half Day Absent (Unpaid)", color: "var(--red)" },
                    { val: "overtime",     label: "⚡ Add Overtime Hours",        color: "var(--brand)" },
                    { val: "extra_hours",  label: "➕ Add Extra Hours",           color: "var(--blue)" },
                  ].map(opt => (
                    <button
                      key={opt.val}
                      className={`pr-bulk-type-btn ${bulkForm.type === opt.val ? "active" : ""}`}
                      style={bulkForm.type === opt.val ? { borderColor: opt.color, color: opt.color, background: opt.color + "18" } : {}}
                      onClick={() => setBulkForm(f => ({ ...f, type: opt.val }))}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hours input for OT / extra */}
              {(bulkForm.type === "overtime" || bulkForm.type === "extra_hours") && (
                <div className="pr-field" style={{ marginBottom: 16 }}>
                  <label>Hours to Add</label>
                  <input type="number" min="0.5" step="0.5" value={bulkForm.hours || ""}
                    onChange={e => setBulkForm(f => ({ ...f, hours: e.target.value }))}
                    style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-body)", fontSize: 13 }}
                    placeholder="e.g. 2" />
                </div>
              )}

              {/* Note */}
              <div className="pr-field" style={{ marginBottom: 16 }}>
                <label>Note (optional)</label>
                <input type="text" value={bulkForm.note} placeholder="e.g. National Holiday, Project Crunch"
                  onChange={e => setBulkForm(f => ({ ...f, note: e.target.value }))}
                  style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-body)", fontSize: 13 }} />
              </div>

              {/* Employee selection */}
              <div className="pr-field">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <label>Select Employees</label>
                  <button className="pr-btn pr-btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={selectAllBulk}>Select All</button>
                </div>
                <div className="pr-bulk-emp-list">
                  {localAtt.map(r => (
                    <label key={r.employee_id} className={`pr-bulk-emp-row ${bulkForm.employees.includes(r.employee_id) ? "selected" : ""}`}>
                      <input type="checkbox" checked={bulkForm.employees.includes(r.employee_id)} onChange={() => toggleBulkEmp(r.employee_id)} />
                      <div className="pr-avatar sm">{avatar(r.employee_name)}</div>
                      <div>
                        <div className="pr-emp-name" style={{ fontSize: 13 }}>{r.employee_name}</div>
                        <div className="pr-emp-role">{r.designation}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="pr-modal-footer">
              <button className="pr-btn pr-btn-ghost" onClick={() => setBulkModal(false)}>Cancel</button>
              <button className="pr-btn pr-btn-primary" onClick={applyBulk} disabled={saving || bulkForm.employees.length === 0}>
                {saving ? "Applying…" : `Apply to ${bulkForm.employees.length} Employee(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   RUN PAYROLL
══════════════════════════════════ */
function RunPayroll({ employees, payrolls, settings, headers, month, year, attendance, onDone, onError }) {
  const [rows, setRows]         = useState([]);
  const [saving, setSaving]     = useState(false);
  const [calcDone, setCalcDone] = useState(false);

  const existingIds = new Set(payrolls.map(p => p.employee_id));
  const eligible    = employees.filter(e => !existingIds.has(e.id));
  const totalDays   = monthDays(year, month);

  useEffect(() => {
    // Merge attendance data into rows
    const attMap = {};
    attendance.forEach(a => { attMap[a.employee_id] = a; });

    setRows(eligible.map(e => {
      const att = attMap[e.id] || {};
      const fullLeave   = att.full_day_leaves  ?? 0;
      const halfLeave   = att.half_day_leaves  ?? 0;
      const unpaidFull  = att.unpaid_full      ?? 0;
      const unpaidHalf  = att.unpaid_half      ?? 0;
      const effectiveWD = Math.max(0, totalDays - fullLeave - halfLeave * 0.5);
      const stdHours    = effectiveWD * (settings?.working_hours_per_day ?? 8);
      return {
        id:             e.id,
        name:           `${e.first_name} ${e.last_name}`,
        designation:    e.designation || "",
        department:     e.department  || "",
        basic_salary:   e.basic_salary || 0,
        total_days:     totalDays,
        working_days:   effectiveWD,
        full_day_leaves: fullLeave,
        half_day_leaves: halfLeave,
        unpaid_full:    unpaidFull,
        unpaid_half:    unpaidHalf,
        hours_worked:   stdHours,
        standard_hours: stdHours,
        overtime_hours: att.overtime_hours ?? 0,
        extra_hours:    att.extra_hours    ?? 0,
        hra:            e.hra              || 0,
        transport:      e.transport_allowance || 0,
        other_allow:    e.other_allowances || 0,
        pf:0, esi:0, tds:0, other_deduct:0,
        gross_salary:0, total_deductions:0, net_salary:0,
        notes: att.note || "",
      };
    }));
    setCalcDone(false);
  }, [employees.length, month, year, attendance.length]);

  function update(id, field, val) {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: isNaN(val) ? val : Number(val) };
      return updated;
    }));
    setCalcDone(false);
  }

  function calculate() {
    const s = settings || defaultSettings;
    setRows(prev => prev.map(r => {
      const totalD      = r.total_days;
      const perDay      = r.basic_salary / totalD;
      const perHour     = r.basic_salary / (totalD * (s.working_hours_per_day ?? 8));
      // Effective days: all days minus ONLY unpaid absences
      const effectiveDays = Math.max(0, totalD - r.full_day_leaves - r.half_day_leaves * 0.5 - r.unpaid_full - r.unpaid_half * 0.5);
      const earnedBasic   = Math.round(perDay * effectiveDays);
      const overtimePay   = Math.round(perHour * (s.overtime_multiplier ?? 1.5) * ((r.overtime_hours ?? 0) + (r.extra_hours ?? 0)));
      const gross         = earnedBasic + r.hra + r.transport + r.other_allow + overtimePay;
      const pf            = s.pf_enabled  ? Math.round(earnedBasic * (s.pf_rate / 100))  : 0;
      const esi           = s.esi_enabled && gross <= 21000 ? Math.round(gross * (s.esi_rate / 100)) : 0;
      const tds           = s.tds_enabled ? Math.round(gross * (s.tds_rate / 100)) : 0;
      const totalDeduct   = pf + esi + tds + (r.other_deduct ?? 0);
      const net           = Math.round(gross - totalDeduct);
      return { ...r, pf, esi, tds, gross_salary: Math.round(gross), total_deductions: totalDeduct, net_salary: net };
    }));
    setCalcDone(true);
  }

  async function submit() {
    if (!calcDone) return onError("Please calculate salaries before submitting.");
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/payroll/run`, {
        method: "POST",
        headers,
        body: JSON.stringify({ month: month + 1, year, records: rows }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onDone();
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (eligible.length === 0 && payrolls.length > 0) {
    return (
      <div className="pr-card pr-empty-state">
        <div className="pr-empty-icon">✅</div>
        <h3>Payroll already processed</h3>
        <p>All employees have been paid for {months[month]} {year}. Check Pay Records for details.</p>
      </div>
    );
  }

  return (
    <div className="pr-run">
      <div className="pr-run-header">
        <div className="pr-run-info">
          <span>Processing <strong>{rows.length}</strong> employees</span>
          <span>Period: <strong>{months[month]} {year}</strong></span>
          <span>Month Days: <strong>{totalDays}</strong></span>
        </div>
        <div className="pr-run-actions">
          <button className="pr-btn pr-btn-secondary" onClick={calculate}>⚡ Calculate Salaries</button>
          <button className="pr-btn pr-btn-primary" onClick={submit} disabled={saving || !calcDone}>
            {saving ? "Processing…" : "▶ Submit Payroll"}
          </button>
        </div>
      </div>

      {calcDone && (
        <div className="pr-calc-summary">
          <span>💰 Total Gross: <strong>{fmt(rows.reduce((s,r)=>s+r.gross_salary,0))}</strong></span>
          <span>📉 Total Deductions: <strong>{fmt(rows.reduce((s,r)=>s+r.total_deductions,0))}</strong></span>
          <span>✅ Net Payable: <strong>{fmt(rows.reduce((s,r)=>s+r.net_salary,0))}</strong></span>
        </div>
      )}

      <div className="pr-info-strip" style={{ marginBottom: 12 }}>
        <span>ℹ️ Attendance data has been pre-filled from the Attendance Manager. You can adjust values below before calculating.</span>
      </div>

      <div className="pr-run-table-wrap">
        <table className="pr-run-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Basic (₹)</th>
              <th>Total Days</th>
              <th>Paid Full Leave</th>
              <th>Paid Half Leave</th>
              <th>Unpaid Full</th>
              <th>Unpaid Half</th>
              <th>OT Hours</th>
              <th>Extra Hrs</th>
              <th>HRA (₹)</th>
              <th>Transport (₹)</th>
              <th>Other Allow (₹)</th>
              <th>Other Deduct (₹)</th>
              {calcDone && <><th>Eff. Days</th><th>Gross</th><th>Deductions</th><th>Net Pay</th></>}
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>
                  <div className="pr-emp-cell">
                    <div className="pr-avatar sm">{avatar(r.name)}</div>
                    <div>
                      <div className="pr-emp-name">{r.name}</div>
                      <div className="pr-emp-role">{r.designation}</div>
                    </div>
                  </div>
                </td>
                <td><input className="pr-input-cell" type="number" value={r.basic_salary} onChange={e=>update(r.id,"basic_salary",e.target.value)} /></td>
                <td><input className="pr-input-cell narrow" type="number" min="1" max="31" value={r.total_days} onChange={e=>update(r.id,"total_days",e.target.value)} /></td>
                <td>
                  <input className="pr-input-cell narrow leave-input-full" type="number" min="0"
                    value={r.full_day_leaves} onChange={e=>update(r.id,"full_day_leaves",e.target.value)} />
                </td>
                <td>
                  <input className="pr-input-cell narrow leave-input-half" type="number" min="0"
                    value={r.half_day_leaves} onChange={e=>update(r.id,"half_day_leaves",e.target.value)} />
                </td>
                <td>
                  <input className="pr-input-cell narrow leave-input-unpaid" type="number" min="0"
                    value={r.unpaid_full} onChange={e=>update(r.id,"unpaid_full",e.target.value)} />
                </td>
                <td>
                  <input className="pr-input-cell narrow leave-input-unpaid" type="number" min="0"
                    value={r.unpaid_half} onChange={e=>update(r.id,"unpaid_half",e.target.value)} />
                </td>
                <td><input className="pr-input-cell narrow" type="number" min="0" step="0.5" value={r.overtime_hours} onChange={e=>update(r.id,"overtime_hours",e.target.value)} /></td>
                <td><input className="pr-input-cell narrow" type="number" min="0" step="0.5" value={r.extra_hours} onChange={e=>update(r.id,"extra_hours",e.target.value)} /></td>
                <td><input className="pr-input-cell" type="number" min="0" value={r.hra} onChange={e=>update(r.id,"hra",e.target.value)} /></td>
                <td><input className="pr-input-cell" type="number" min="0" value={r.transport} onChange={e=>update(r.id,"transport",e.target.value)} /></td>
                <td><input className="pr-input-cell" type="number" min="0" value={r.other_allow} onChange={e=>update(r.id,"other_allow",e.target.value)} /></td>
                <td><input className="pr-input-cell" type="number" min="0" value={r.other_deduct} onChange={e=>update(r.id,"other_deduct",e.target.value)} /></td>
                {calcDone && (
                  <>
                    <td className="pr-money" style={{ color: "var(--ink-2)" }}>
                      {(r.total_days - r.full_day_leaves - r.half_day_leaves * 0.5 - r.unpaid_full - r.unpaid_half * 0.5).toFixed(1)}
                    </td>
                    <td className="pr-money">{fmt(r.gross_salary)}</td>
                    <td className="pr-money red">{fmt(r.total_deductions)}</td>
                    <td className="pr-money green fw">{fmt(r.net_salary)}</td>
                  </>
                )}
                <td><input className="pr-input-cell wide" type="text" value={r.notes} placeholder="Optional note" onChange={e=>update(r.id,"notes",e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   PAY RECORDS
══════════════════════════════════ */
function PayRecords({ payrolls, headers, onUpdate, onToast, month, year }) {
  const [search, setSearch]     = useState("");
  const [detail, setDetail]     = useState(null);
  const [updating, setUpdating] = useState(false);

  const filtered = payrolls.filter(p =>
    (p.employee_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.department    || "").toLowerCase().includes(search.toLowerCase())
  );

  async function markPaid(id) {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/api/payroll/records/${id}/pay`, { method: "PATCH", headers });
      if (!res.ok) throw new Error("Failed to mark paid");
      onToast("Marked as paid!");
      onUpdate();
    } catch(e) { onToast(e.message, "error"); }
    finally { setUpdating(false); }
  }

  async function deleteRecord(id) {
    if (!window.confirm("Delete this payroll record?")) return;
    try {
      const res = await fetch(`${API}/api/payroll/records/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Delete failed");
      onToast("Record deleted.");
      onUpdate();
    } catch(e) { onToast(e.message, "error"); }
  }

  return (
    <div className="pr-records">
      <div className="pr-toolbar">
        <div className="pr-search-wrap">
          <span className="pr-search-icon">🔍</span>
          <input className="pr-search" placeholder="Search employee, department…" value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <div className="pr-toolbar-right">
          <span className="pr-chip">{filtered.length} records</span>
          <span className="pr-chip green">{filtered.filter(p=>p.status==="paid").length} paid</span>
          <span className="pr-chip amber">{filtered.filter(p=>p.status!=="paid").length} pending</span>
        </div>
      </div>

      <div className="pr-table-wrap">
        <table className="pr-table">
          <thead>
            <tr>
              <th>Employee</th><th>Dept</th><th>Working Days</th><th>Leaves</th><th>Hours</th>
              <th>Gross</th><th>Deductions</th><th>Net Pay</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} className="pr-empty">No records found.</td></tr>
            ) : filtered.map(p => (
              <tr key={p.id}>
                <td>
                  <div className="pr-emp-cell">
                    <div className="pr-avatar">{avatar(p.employee_name)}</div>
                    <div>
                      <div className="pr-emp-name">{p.employee_name}</div>
                      <div className="pr-emp-role">{p.designation}</div>
                    </div>
                  </div>
                </td>
                <td>{p.department || "—"}</td>
                <td>{p.working_days}</td>
                <td>
                  <div className="pr-leave-summary">
                    {(p.full_day_leaves || 0) > 0 && <span className="pr-leave-chip full">{p.full_day_leaves}F</span>}
                    {(p.half_day_leaves || 0) > 0 && <span className="pr-leave-chip half">{p.half_day_leaves}H</span>}
                    {(p.unpaid_full || 0) > 0 && <span className="pr-leave-chip unpaid">{p.unpaid_full}U</span>}
                    {!(p.full_day_leaves || p.half_day_leaves || p.unpaid_full) && <span className="pr-leave-chip none">—</span>}
                  </div>
                </td>
                <td>{fmtHr(p.hours_worked)}</td>
                <td className="pr-money">{fmt(p.gross_salary)}</td>
                <td className="pr-money red">{fmt(p.total_deductions)}</td>
                <td className="pr-money green fw">{fmt(p.net_salary)}</td>
                <td><StatusBadge status={p.status} /></td>
                <td>
                  <div className="pr-row-actions">
                    <button className="pr-act-btn info" onClick={() => setDetail(p)} title="View payslip">👁</button>
                    {p.status !== "paid" && (
                      <button className="pr-act-btn success" onClick={() => markPaid(p.id)} disabled={updating} title="Mark paid">✓</button>
                    )}
                    <button className="pr-act-btn danger" onClick={() => deleteRecord(p.id)} title="Delete">✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && <PayslipModal record={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

/* ── Payslip modal ── */
function PayslipModal({ record: r, onClose }) {
  return (
    <div className="pr-modal-overlay" onClick={onClose}>
      <div className="pr-modal" onClick={e => e.stopPropagation()}>
        <div className="pr-modal-head">
          <h3>Payslip — {r.employee_name}</h3>
          <button className="pr-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="pr-payslip">
          <div className="pr-payslip-hero">
            <div className="pr-avatar lg">{avatar(r.employee_name)}</div>
            <div>
              <div className="pr-ps-name">{r.employee_name}</div>
              <div className="pr-ps-role">{r.designation} · {r.department}</div>
              <StatusBadge status={r.status} />
            </div>
          </div>

          <div className="pr-ps-period">
            <span>Period: <strong>{r.pay_month}/{r.pay_year}</strong></span>
            <span>Total Days: <strong>{r.total_days ?? monthDays(r.pay_year, (r.pay_month ?? 1) - 1)}</strong></span>
            <span>Working Days: <strong>{r.working_days}</strong></span>
            <span>Hours: <strong>{fmtHr(r.hours_worked)}</strong></span>
            {(r.overtime_hours || 0) > 0 && <span>OT Hours: <strong>{fmtHr(r.overtime_hours)}</strong></span>}
            {(r.full_day_leaves || 0) > 0  && <span>Paid Full Leaves: <strong>{r.full_day_leaves}</strong></span>}
            {(r.half_day_leaves || 0) > 0  && <span>Paid Half Leaves: <strong>{r.half_day_leaves}</strong></span>}
            {(r.unpaid_full || 0) > 0      && <span className="red">Unpaid Absent: <strong>{r.unpaid_full}</strong></span>}
          </div>

          <div className="pr-ps-grid">
            <div className="pr-ps-col earn">
              <div className="pr-ps-col-head">Earnings</div>
              {[
                ["Basic Salary",    r.basic_salary],
                ["HRA",             r.hra],
                ["Transport",       r.transport_allowance],
                ["Other Allow.",    r.other_allowances],
                ["Overtime Pay",    r.overtime_pay],
              ].filter(([,v]) => v > 0).map(([k,v]) => (
                <div className="pr-ps-row" key={k}>
                  <span>{k}</span><span>{fmt(v)}</span>
                </div>
              ))}
              <div className="pr-ps-total"><span>Gross</span><span>{fmt(r.gross_salary)}</span></div>
            </div>
            <div className="pr-ps-col deduct">
              <div className="pr-ps-col-head">Deductions</div>
              {[
                ["PF",              r.pf_deduction],
                ["ESI",             r.esi_deduction],
                ["TDS",             r.tds_deduction],
                ["Other Deductions",r.other_deductions],
              ].filter(([,v]) => v > 0).map(([k,v]) => (
                <div className="pr-ps-row" key={k}>
                  <span>{k}</span><span className="red">{fmt(v)}</span>
                </div>
              ))}
              <div className="pr-ps-total"><span>Total Deductions</span><span className="red">{fmt(r.total_deductions)}</span></div>
            </div>
          </div>

          <div className="pr-ps-net">
            <span>Net Pay</span>
            <span>{fmt(r.net_salary)}</span>
          </div>
          {r.notes && <div className="pr-ps-notes">📝 {r.notes}</div>}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   EMPLOYEES
══════════════════════════════════ */
function Employees({ employees, headers, onUpdate, onToast }) {
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [search, setSearch]       = useState("");
  const [saving, setSaving]       = useState(false);
  const emptyForm = { first_name:"",last_name:"",email:"",designation:"",department:"",basic_salary:"",hra:"",transport_allowance:"",other_allowances:"",joining_date:"",employee_code:"" };
  const [form, setForm]           = useState(emptyForm);

  const filtered = employees.filter(e =>
    `${e.first_name} ${e.last_name} ${e.designation} ${e.department}`.toLowerCase().includes(search.toLowerCase())
  );

  function openAdd()  { setForm(emptyForm); setEditing(null); setShowForm(true); }
  function openEdit(e){ setForm({...e, basic_salary:e.basic_salary||"", hra:e.hra||"", transport_allowance:e.transport_allowance||"", other_allowances:e.other_allowances||"" }); setEditing(e.id); setShowForm(true); }

  async function save() {
    setSaving(true);
    try {
      const url    = editing ? `${API}/api/payroll/employees/${editing}` : `${API}/api/payroll/employees`;
      const method = editing ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const data   = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      onToast(editing ? "Employee updated!" : "Employee added!");
      setShowForm(false);
      onUpdate();
    } catch(e) { onToast(e.message, "error"); }
    finally { setSaving(false); }
  }

  async function del(id) {
    if (!window.confirm("Remove this employee from payroll?")) return;
    try {
      const res = await fetch(`${API}/api/payroll/employees/${id}`, { method:"DELETE", headers });
      if (!res.ok) throw new Error("Delete failed");
      onToast("Employee removed.");
      onUpdate();
    } catch(e) { onToast(e.message, "error"); }
  }

  return (
    <div className="pr-employees">
      <div className="pr-toolbar">
        <div className="pr-search-wrap">
          <span className="pr-search-icon">🔍</span>
          <input className="pr-search" placeholder="Search name, designation, dept…" value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <div className="pr-toolbar-right">
          <span className="pr-chip">{filtered.length} employees</span>
          <button className="pr-btn pr-btn-primary" onClick={openAdd}>+ Add Employee</button>
        </div>
      </div>

      <div className="pr-emp-grid">
        {filtered.map(e => (
          <div className="pr-emp-card" key={e.id}>
            <div className="pr-emp-card-top">
              <div className="pr-avatar lg">{avatar(`${e.first_name} ${e.last_name}`)}</div>
              <div className="pr-emp-card-info">
                <div className="pr-emp-card-name">{e.first_name} {e.last_name}</div>
                <div className="pr-emp-card-role">{e.designation}</div>
                <div className="pr-emp-card-dept">{e.department}</div>
              </div>
            </div>
            <div className="pr-emp-card-salary">
              <div><span>Basic</span><strong>{fmt(e.basic_salary)}</strong></div>
              <div><span>HRA</span><strong>{fmt(e.hra)}</strong></div>
              <div><span>Transport</span><strong>{fmt(e.transport_allowance)}</strong></div>
            </div>
            {e.employee_code && <div className="pr-emp-code">ID: {e.employee_code}</div>}
            <div className="pr-emp-card-actions">
              <button className="pr-act-btn info"   onClick={() => openEdit(e)} title="Edit">✏</button>
              <button className="pr-act-btn danger" onClick={() => del(e.id)}   title="Remove">✕</button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="pr-empty">No employees found. Add employees to get started.</div>}
      </div>

      {showForm && (
        <div className="pr-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="pr-modal" onClick={e=>e.stopPropagation()}>
            <div className="pr-modal-head">
              <h3>{editing ? "Edit Employee" : "Add Employee"}</h3>
              <button className="pr-modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="pr-form-grid">
              {[
                ["first_name","First Name","text"],["last_name","Last Name","text"],
                ["email","Email","email"],["employee_code","Employee Code","text"],
                ["designation","Designation","text"],["department","Department","text"],
                ["joining_date","Joining Date","date"],
              ].map(([k,l,t]) => (
                <div className="pr-field" key={k}>
                  <label>{l}</label>
                  <input type={t} value={form[k]||""} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} />
                </div>
              ))}
              <div className="pr-field-section">Salary Structure (Monthly ₹)</div>
              {[
                ["basic_salary","Basic Salary"],["hra","HRA"],
                ["transport_allowance","Transport Allowance"],["other_allowances","Other Allowances"],
              ].map(([k,l]) => (
                <div className="pr-field" key={k}>
                  <label>{l}</label>
                  <input type="number" min="0" value={form[k]||""} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} />
                </div>
              ))}
            </div>
            <div className="pr-modal-footer">
              <button className="pr-btn pr-btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="pr-btn pr-btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Employee"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   SETTINGS
══════════════════════════════════ */
const defaultSettings = {
  pf_enabled: true,  pf_rate: 12,
  esi_enabled: true, esi_rate: 0.75,
  tds_enabled: false, tds_rate: 0,
  overtime_multiplier: 1.5,
  working_hours_per_day: 8,
  company_name: "",
  pay_day: 1,
};

function Settings({ settings: initial, headers, onUpdate, onToast }) {
  const [form, setForm] = useState(initial || defaultSettings);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/payroll/settings`, {
        method: "POST", headers,
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Save failed");
      onToast("Settings saved!");
      onUpdate();
    } catch(e) { onToast(e.message, "error"); }
    finally { setSaving(false); }
  }

  const toggle = k => setForm(f => ({ ...f, [k]: !f[k] }));
  const set    = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="pr-settings">
      <div className="pr-card">
        <div className="pr-card-head"><span className="pr-card-title">Company & Pay Settings</span></div>
        <div className="pr-form-grid">
          <div className="pr-field full">
            <label>Company Name</label>
            <input type="text" value={form.company_name||""} onChange={e=>set("company_name",e.target.value)} placeholder="Your company name" />
          </div>
          <div className="pr-field">
            <label>Salary Pay Day (of month)</label>
            <input type="number" min="1" max="31" value={form.pay_day||1} onChange={e=>set("pay_day",Number(e.target.value))} />
          </div>
          <div className="pr-field">
            <label>Working Hours / Day</label>
            <input type="number" min="1" max="24" value={form.working_hours_per_day||8} onChange={e=>set("working_hours_per_day",Number(e.target.value))} />
            <span className="pr-field-hint">Standard hours used for overtime calculation</span>
          </div>
          <div className="pr-field">
            <label>Overtime Multiplier</label>
            <input type="number" min="1" step="0.1" value={form.overtime_multiplier||1.5} onChange={e=>set("overtime_multiplier",Number(e.target.value))} />
            <span className="pr-field-hint">e.g. 1.5 = 1.5× hourly rate for overtime</span>
          </div>
        </div>
      </div>

      <div className="pr-card">
        <div className="pr-card-head"><span className="pr-card-title">Leave Policy</span></div>
        <div className="pr-form-grid">
          <div className="pr-field full">
            <div className="pr-settings-info-box">
              <strong>📋 How Leaves Affect Salary</strong>
              <p>Paid leaves (full/half day) keep the employee's salary unaffected — they are still counted as working days. Only <em>unpaid absences</em> deduct from earned basic salary. Half-day values count as 0.5 days.</p>
            </div>
          </div>
          <div className="pr-field">
            <label>Salary Calculation Basis</label>
            <select
              value={form.salary_basis || "calendar"}
              onChange={e => set("salary_basis", e.target.value)}
              style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-body)", fontSize: 13 }}
            >
              <option value="calendar">Calendar Days (default — actual days in month)</option>
              <option value="26">26 Fixed Days/Month</option>
              <option value="30">30 Fixed Days/Month</option>
            </select>
            <span className="pr-field-hint">Determines per-day rate for salary proration</span>
          </div>
        </div>
      </div>

      <div className="pr-card">
        <div className="pr-card-head"><span className="pr-card-title">Statutory Deductions</span></div>
        <div className="pr-form-grid">
          {/* PF */}
          <div className="pr-field">
            <label className="pr-toggle-label">
              <span>Provident Fund (PF)</span>
              <button className={`pr-toggle ${form.pf_enabled?"on":""}`} onClick={()=>toggle("pf_enabled")}>{form.pf_enabled?"ON":"OFF"}</button>
            </label>
          </div>
          <div className="pr-field">
            <label>PF Rate (%)</label>
            <input type="number" min="0" max="100" step="0.1" value={form.pf_rate||12} onChange={e=>set("pf_rate",Number(e.target.value))} disabled={!form.pf_enabled} />
            <span className="pr-field-hint">Applied on earned basic salary</span>
          </div>

          {/* ESI */}
          <div className="pr-field">
            <label className="pr-toggle-label">
              <span>ESI (for gross ≤ ₹21,000)</span>
              <button className={`pr-toggle ${form.esi_enabled?"on":""}`} onClick={()=>toggle("esi_enabled")}>{form.esi_enabled?"ON":"OFF"}</button>
            </label>
          </div>
          <div className="pr-field">
            <label>ESI Rate (%)</label>
            <input type="number" min="0" step="0.01" value={form.esi_rate||0.75} onChange={e=>set("esi_rate",Number(e.target.value))} disabled={!form.esi_enabled} />
          </div>

          {/* TDS */}
          <div className="pr-field">
            <label className="pr-toggle-label">
              <span>TDS</span>
              <button className={`pr-toggle ${form.tds_enabled?"on":""}`} onClick={()=>toggle("tds_enabled")}>{form.tds_enabled?"ON":"OFF"}</button>
            </label>
          </div>
          <div className="pr-field">
            <label>TDS Rate (%)</label>
            <input type="number" min="0" step="0.1" value={form.tds_rate||0} onChange={e=>set("tds_rate",Number(e.target.value))} disabled={!form.tds_enabled} />
          </div>
        </div>
      </div>

      <div className="pr-settings-footer">
        <button className="pr-btn pr-btn-primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "💾 Save Settings"}
        </button>
      </div>
    </div>
  );
}
